import { useCallback, useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';

type UseScrubberOptions = {
  minThumbPercent?: number;
};

/**
 * Drag-to-seek logic for the carousel scrubber bar. Previously copy-pasted
 * identically across DetailView/LetrasView/SilabasView — see CardCarousel.
 */
export function useScrubber(
  count: number,
  currentIdx: number,
  scrollToIndex: (idx: number) => void,
  { minThumbPercent = 8 }: UseScrubberOptions = {}
) {
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef(false);

  const seekToPointer = useCallback((clientX: number) => {
    const sb = scrollbarRef.current;
    if (!sb) return;
    const rect = sb.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const idx = Math.round(fraction * (count - 1));
    scrollToIndex(idx);
  }, [count, scrollToIndex]);

  const onPointerDown = useCallback((e: ReactPointerEvent) => {
    e.preventDefault();
    dragRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    seekToPointer(e.clientX);
  }, [seekToPointer]);

  const onPointerMove = useCallback((e: ReactPointerEvent) => {
    if (!dragRef.current) return;
    seekToPointer(e.clientX);
  }, [seekToPointer]);

  const onPointerUp = useCallback((e: ReactPointerEvent) => {
    dragRef.current = false;
    const t = e.target as HTMLElement;
    if (t.hasPointerCapture(e.pointerId)) t.releasePointerCapture(e.pointerId);
  }, []);

  const thumbPercent = Math.max(minThumbPercent, 100 / count);
  const thumbStyle: CSSProperties = {
    width: `${thumbPercent}%`,
    left: `${(currentIdx / Math.max(1, count - 1)) * (100 - thumbPercent)}%`,
  };

  return {
    scrollbarRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    thumbStyle,
  };
}

export type UseScrubberReturn = ReturnType<typeof useScrubber>;
